import { computed, inject, Injectable, signal } from '@angular/core';
import {
  ENVIRONMENT,
  getAccountStanding,
  getEncryptionValueForLoan,
  getFullAddress,
  getLoanNumbersFromProfile,
  getLoanTypeFromProfile,
  HelocDrawEligibilityEnum,
  HelocLoanInfo,
  LoanMessage,
  LoanRequestItem,
  LoanResponse,
  LoanSummary,
  PaperlessInfo,
  PaymentProcessingStatus,
  PaymentTransaction,
  PendingPaymentApiItem,
  UserProfile,
  UserProperty,
} from '@ld.consumer-portal/models';
import {
  AuthService,
  deriveDrawEligibility as deriveDrawEligibilityUtil,
  EnvironmentFeatureService,
  isHelocInHouse as isHelocInHouseUtil,
  isOnlyActivePaymentCountError,
  LoanSelectionService,
  LoggingService,
  shouldShowHelocAmountDue as shouldShowHelocAmountDueUtil,
  ThemeService,
} from '@ld.consumer-portal/utils';
import {
  catchError,
  finalize,
  firstValueFrom,
  forkJoin,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { FeatureFlagService } from '../feature-flag';
import { HelocService } from '../heloc';
import { LoanService } from '../loan';
import { MlaAuthService, MlaBorrowerService, MlaDashboardService } from '../mla';
import { SsoAuthService } from '../origination';
import { PaymentService } from '../payment';
import { ProfileService } from '../profile';
import { AccountModel, OriginationApplicationModel } from './models';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(AuthService);
  private readonly profileService = inject(ProfileService);
  private readonly loanService = inject(LoanService);
  private readonly helocService = inject(HelocService);
  private readonly paymentService = inject(PaymentService);
  private readonly loanSelectionService = inject(LoanSelectionService);
  private readonly mlaDashboardService = inject(MlaDashboardService);
  private readonly mlaAuthService = inject(MlaAuthService);
  private readonly mlaBorrowerService = inject(MlaBorrowerService);
  private readonly ssoAuthService = inject(SsoAuthService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly envFeatureService = inject(EnvironmentFeatureService);
  private readonly loggingService = inject(LoggingService);
  private readonly themeService = inject(ThemeService);

  private mlaAuthToken: string | null = null;

  private cpAuthToken: string | null = null;

  private readonly _userProfile = signal<UserProfile | null>(null);
  readonly userProfile = this._userProfile.asReadonly();

  private readonly _loanResponses = signal<LoanResponse[]>([]);
  readonly loanResponses = this._loanResponses.asReadonly();

  private readonly _paymentHistoryCache = signal<
    Map<string, { posted: PaymentTransaction[]; pending: PendingPaymentApiItem[] }>
  >(new Map());
  readonly paymentHistoryCache = this._paymentHistoryCache.asReadonly();

  private loanSummaryCache: Map<string, LoanSummary> = new Map();

  private accountsCache$: Observable<AccountModel[]> | null = null;

  private originationCache$: Observable<OriginationApplicationModel[]> | null = null;

  private readonly _helocLoanInfoCache = signal<Map<string, HelocLoanInfo>>(new Map());
  readonly helocLoanInfoCache = this._helocLoanInfoCache.asReadonly();

  private readonly _helocLoanInfoLoading = signal(false);
  readonly helocLoanInfoLoading = this._helocLoanInfoLoading.asReadonly();

  private readonly _helocRefreshingLoans = signal<Set<string>>(new Set());
  readonly helocRefreshingLoans = this._helocRefreshingLoans.asReadonly();

  private readonly _helocLoanInfoPendingLoans = signal<Set<string>>(new Set());
  readonly helocLoanInfoPendingLoans = this._helocLoanInfoPendingLoans.asReadonly();

  private readonly _loanMessages = signal<LoanMessage[]>([]);
  readonly loanMessages = this._loanMessages.asReadonly();

  private readonly _isLoadingRemainingLoans = signal(false);
  readonly isLoadingRemainingLoans = this._isLoadingRemainingLoans.asReadonly();

  private readonly _servicingLoanNumbers = signal<string[]>([]);
  readonly servicingLoanNumbers = this._servicingLoanNumbers.asReadonly();

  private readonly _fundedLoanNumbersFoundByEmail = signal<string[]>([]);

  private readonly _originationAppLoanNumbers = signal<string[]>([]);

  private readonly _unlinkedLoansLookupCompleted = signal(false);

  private readonly _unlinkedLoansLookupFailed = signal(false);
  readonly unlinkedLoansLookupFailed = this._unlinkedLoansLookupFailed.asReadonly();

  private readonly _accountsLoaded = signal(false);

  private readonly _originationAppsLoaded = signal(false);

  private readonly _readinessTimedOut = signal(false);

  private readonly _accountsRequested = signal(false);

  // True once the authoritative GetLoans snapshot has settled (success, empty or failure).
  // It is deliberately NOT true for partial snapshots: `loadSingleLoanFirst` populates
  // `_loanResponses` with a single loan while the rest load in the background, and
  // `clearAccountsCache` empties it entirely. Reconciling the email lookup against either
  // of those states makes every other servicing loan look unlinked.
  private readonly _fullLoanSetLoaded = signal(false);

  private readonly _nameSourceLoanResponses = signal<LoanResponse[]>([]);

  /**
   * Latest NON-EMPTY GetLoans snapshot, retained across `clearAccountsCache` so borrower-name
   * resolution keeps a source while a background refresh is in flight. `loanResponses` is
   * purged synchronously on refresh but refilled asynchronously, which would otherwise drop
   * the servicing borrower (name priority #1) and visibly swap the header/welcome name to the
   * origination or auth fallback for the duration of the refetch.
   *
   * Mirrors `setOriginationApplications`, which likewise ignores a transient empty reload.
   * Only cleared by `clearCache` (login/logout).
   */
  readonly nameSourceLoanResponses = this._nameSourceLoanResponses.asReadonly();

  // Origination-only routes (e.g. /origination, /apply) never request accounts, so they must
  // not be blocked on a GetLoans snapshot that will never arrive.
  private readonly fullLoanSnapshotReady = computed(() => !this._accountsRequested() || this._fullLoanSetLoaded());

  readonly unlinkedLoansLookupCompleted = computed(
    () => this._unlinkedLoansLookupCompleted() && this.fullLoanSnapshotReady(),
  );

  private static readonly USER_DISPLAY_READINESS_TIMEOUT_MS = 15_000;

  private readinessTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  // Readiness for the borrower name shown in the global header / dashboard welcome text.
  // Consumers use this to show a loading placeholder until the name source is available,
  // instead of briefly rendering the auth-derived full name before it is superseded.
  // Priority (to avoid unnecessary waiting):
  //   1. Once the readiness timeout elapses, fall back to whatever the name priority list
  //      resolves to so the placeholder can never persist indefinitely.
  //   2. Dashboard flows request accounts (GetLoans): wait for accounts, then if there is a
  //      servicing loan the name comes from it -> ready now; otherwise wait for origination.
  //   3. Origination-only header routes (e.g. /origination, /apply) never request accounts,
  //      so readiness resolves once origination applications have loaded.
  readonly userDisplayDataLoaded = computed(() => {
    if (this._readinessTimedOut()) {
      return true;
    }
    if (this._accountsRequested()) {
      if (!this._accountsLoaded()) {
        return false;
      }
      if (this._loanResponses().length > 0) {
        return true;
      }
      return this._originationAppsLoaded();
    }
    return this._originationAppsLoaded();
  });

  // True when the login email resolved to at least one funded loan number in servicing.
  // Unlike unlinkedLoansFound this does not reconcile against linked/in-flight loans.
  readonly emailFoundInServicing = computed(() => this._fundedLoanNumbersFoundByEmail().length > 0);

  readonly unlinkedLoansFound = computed(() => {
    const fundedLoanNumbersFoundByEmail = this._fundedLoanNumbersFoundByEmail();
    if (fundedLoanNumbersFoundByEmail.length === 0) {
      return false;
    }

    // Never reconcile against a partial or purged loan set, otherwise the "link my loan"
    // prompt flashes while the full GetLoans snapshot is still in flight (the window grows
    // with the number of mortgages/HELOCs on the profile).
    if (!this.fullLoanSnapshotReady()) {
      return false;
    }

    // A funded loan is already accounted for if it is linked (GetLoans) or if it
    // is in flight as an origination application, so neither should be offered to link.
    const accountedForSet = new Set([
      ...this._loanResponses()
        .map((loanResponse) => loanResponse.loan.loanNumber?.trim())
        .filter((loanNumber): loanNumber is string => !!loanNumber),
      ...this._originationAppLoanNumbers(),
    ]);

    return fundedLoanNumbersFoundByEmail.some((loanNumber) => !!loanNumber && !accountedForSet.has(loanNumber.trim()));
  });

  private _loadSingleLoanPromise: Promise<void> | null = null;

  // Single write path for the loan set so the retained name source cannot drift out of sync.
  // An empty set never overwrites the name source: it means "purged/refreshing", not "no loans".
  private setLoanResponses(responses: LoanResponse[]): void {
    this._loanResponses.set(responses);
    if (responses.length > 0) {
      this._nameSourceLoanResponses.set(responses);
    }
  }

  // Starts a one-shot fallback timer for `userDisplayDataLoaded`. If loading stalls, the
  // readiness flips to "ready" after the timeout so the name placeholder can never persist
  // indefinitely and consumers fall back to whatever the name priority list resolves to.
  private startReadinessTimeout(): void {
    if (this.readinessTimeoutHandle || this._readinessTimedOut()) {
      return;
    }
    this.readinessTimeoutHandle = setTimeout(() => {
      this._readinessTimedOut.set(true);
      this.readinessTimeoutHandle = null;
    }, DashboardService.USER_DISPLAY_READINESS_TIMEOUT_MS);
  }

  getUserAccounts(): Observable<AccountModel[]> {
    this._accountsRequested.set(true);
    this.startReadinessTimeout();

    if (this._loanResponses().length > 0 && this._userProfile()) {
      this._accountsLoaded.set(true);
      return of(this.transformLoansToAccounts(this._loanResponses()));
    }

    if (this.accountsCache$) {
      return this.accountsCache$;
    }

    let userId = this.authService.getUserId();

    if (!userId && this.env.mockConfig?.enabled) {
      userId = 'mock-user-id-12345';
    }

    if (!userId) {
      this._accountsLoaded.set(true);
      this._fullLoanSetLoaded.set(true);
      return of([]);
    }

    this.accountsCache$ = from(this.fetchUserProfile(userId)).pipe(
      switchMap((profile) => {
        if (!profile) {
          return of([]);
        }

        this._userProfile.set(profile);

        if (profile.firstName || profile.lastName) {
          const selectedBorrowerName = this.mlaAuthService.getSelectedBorrowerName();
          const cachedBorrower = this.mlaBorrowerService.getCachedPrimaryBorrower();
          const resolvedFirstName = selectedBorrowerName?.firstName || cachedBorrower?.firstName || profile.firstName;
          const resolvedLastName = selectedBorrowerName?.lastName || cachedBorrower?.lastName || profile.lastName;
          this.authService.setUserInfo(profile.globalUserID, resolvedFirstName, resolvedLastName, profile.email);
        }

        const loanRequests = this.buildLoanRequests(profile);

        if (loanRequests.length === 0) {
          return of([]);
        }

        return from(this.loanService.getLoans(loanRequests)).pipe(
          map((loanResponses) => this.sortLoanResponsesByType(loanResponses)),
          tap((sortedResponses) => {
            this.setLoanResponses(sortedResponses);
            this._fullLoanSetLoaded.set(true);
            this.loanSelectionService.setAllLoanResponses(sortedResponses, this._userProfile());
          }),
          switchMap((sortedResponses) => {
            return from(this.loadPendingPaymentsForLoansAsync(sortedResponses)).pipe(
              switchMap(() => {
                this.loadLoanSummaries(loanRequests).subscribe();
                this.loadLoanMessages(loanRequests).subscribe();
                this.featureFlagService
                  .loadFlags(loanRequests)
                  .catch((error) =>
                    this.loggingService.logException(error, '[DashboardService] Failed to load feature flags'),
                  );
                this.loadHelocLoanInfo(sortedResponses).subscribe();
                return of(sortedResponses);
              }),
            );
          }),
        );
      }),
      map((loanResponses) => {
        return this.transformLoansToAccounts(loanResponses);
      }),
      tap({
        next: () => {
          this._accountsLoaded.set(true);
          // Covers the paths that resolve without calling GetLoans (no profile / JV user,
          // or a profile with no linkable loan requests): the empty set is authoritative.
          this._fullLoanSetLoaded.set(true);
        },
        complete: () => {
          this.accountsCache$ = null;
        },
        error: () => {
          this.accountsCache$ = null;
          this._accountsLoaded.set(true);
          // GetLoans failed, so the loan set will not get any more complete than it is now.
          // Unblock reconciliation rather than suppressing the CTA indefinitely.
          this._fullLoanSetLoaded.set(true);
        },
      }),
      shareReplay(1),
      // NOTE: We intentionally do NOT catch errors here.
      // Errors should propagate to the caller (guards, components) so they can
      // properly handle failed API calls (e.g., redirect to login on CORS/auth errors)
    );

    return this.accountsCache$;
  }

  getOriginationApplications(): Observable<OriginationApplicationModel[]> {
    // Arm the readiness fallback for origination-only header routes (e.g. /origination,
    // /apply) that render the global header but never request servicing accounts.
    this.startReadinessTimeout();

    if (this.originationCache$) {
      return this.originationCache$;
    }

    const b2cIdToken = this.authService.getB2cIdToken();
    const userEmail = this.authService.getUserEmail();

    let source$: Observable<OriginationApplicationModel[]>;

    if (!b2cIdToken || !userEmail) {
      this.loggingService.logWarning('[DashboardService] No B2C ID token or email available for MLA auth');
      source$ = this.getOriginationApplicationsFromMla();
    } else {
      this.loggingService.logInfo(
        `[DashboardService] getOriginationApplications: calling authenticateOrRegisterB2CUser | email=${userEmail}`,
      );
      source$ = from(this.mlaAuthService.authenticateOrRegisterB2CUser(b2cIdToken, userEmail)).pipe(
        switchMap((mlaSession) => {
          if (!mlaSession) {
            return this.getOriginationApplicationsFromMla();
          }

          this.mlaAuthToken = mlaSession.token;

          return this.getOriginationApplicationsFromMla();
        }),
        catchError((error) => {
          this.loggingService.logException(error, '[DashboardService] MLA B2C auth error');
          return this.getOriginationApplicationsFromMla();
        }),
      );
    }

    this.originationCache$ = source$.pipe(
      tap({
        next: () => {
          this._originationAppsLoaded.set(true);
        },
        complete: () => {
          this.originationCache$ = null;
        },
        error: () => {
          this.originationCache$ = null;
          this._originationAppsLoaded.set(true);
        },
      }),
      shareReplay(1),
    );

    return this.originationCache$;
  }

  private async getOriginationApplicationsFromMlaAsync(): Promise<OriginationApplicationModel[]> {
    try {
      const mlaLoans = await this.mlaDashboardService.getLoanApplications();
      const applications = mlaLoans.map(
        (loan): OriginationApplicationModel => ({
          id: loan.id,
          loanNumber: loan.loanNumber,
          propertyAddress: loan.propertyAddress,
          status: loan.status,
          statusLabel: loan.statusLabel,
          loanType: loan.loanType,
          tasksCount: loan.tasksCount,
          newMonthlyPayment: loan.newMonthlyPayment,
          interestRate: loan.newInterestRate,
          monthlySavings: loan.monthlySavings,
          estimatedClosingDate: loan.estimatedClosingDate,
          redirectURL: loan.redirectURL,
          lastModifiedDate: loan.lastModifiedDate,
          createdDate: loan.createdDate,
          servicingEnabledDate: loan.servicingEnabledDate,
          borrowerEmails: loan.borrowerEmails,
          borrowerNames: loan.borrowerNames,
          currentUserIsPrimaryBorrower: loan.currentUserIsPrimaryBorrower,
        }),
      );

      return this.filterStaleApplications(applications);
    } catch (error) {
      this.loggingService.logException(error, 'Failed to get origination applications from MLA:');
      return [];
    }
  }

  private filterStaleApplications(applications: OriginationApplicationModel[]): OriginationApplicationModel[] {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setDate(1);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    sixMonthsAgo.setHours(0, 0, 0, 0);

    return applications.filter((app) => {
      if (this.hasDocutechBorrowerEmail(app)) {
        return false;
      }
      if (app.status === 'Paying') {
        return true;
      }
      if (this.hasTestBorrowerEmail(app)) {
        return true;
      }
      if (!app.lastModifiedDate || isNaN(new Date(app.lastModifiedDate).getTime())) {
        return true;
      }
      return new Date(app.lastModifiedDate) >= sixMonthsAgo;
    });
  }

  private hasTestBorrowerEmail(app: OriginationApplicationModel): boolean {
    return !!app.borrowerEmails?.some((email) => email.toLowerCase().endsWith('@yopmail.com'));
  }

  private hasDocutechBorrowerEmail(app: OriginationApplicationModel): boolean {
    return !!app.borrowerEmails?.some((email) => email.toLowerCase() === 'paperout@docutech.com');
  }

  private getOriginationApplicationsFromMla(): Observable<OriginationApplicationModel[]> {
    return from(this.getOriginationApplicationsFromMlaAsync());
  }

  private async exchangeCpSsoInBackground(loanId: string): Promise<void> {
    this.loggingService.logInfo(`[DashboardService] exchangeCpSsoInBackground called for loanId=${loanId}`);
    try {
      const borrowerInfo = await this.mlaBorrowerService.getBorrowerInfo();

      const mlaBorrowerId = sessionStorage.getItem('mla_borrowerId');
      const validMlaBorrowerId = mlaBorrowerId && mlaBorrowerId !== 'undefined' ? mlaBorrowerId : null;
      const selectedBorrower = validMlaBorrowerId
        ? borrowerInfo.find(
            (b) => b.borrowerIds?.includes(validMlaBorrowerId) || b.borrowerInfoId === validMlaBorrowerId,
          )
        : null;
      const borrower = selectedBorrower || borrowerInfo.find((b) => b.isPrimary);

      const dashboardBorrowerId = this.mlaDashboardService.getLoggedInBorrowerIdForLoan(loanId);
      const loanDetail = borrower?.loanDetails?.find((ld) => ld.loanId === loanId);
      const loanSpecificBorrowerId =
        loanDetail?.borrowers?.find((id) => borrower?.borrowerIds?.includes(id)) ?? loanDetail?.borrowers?.[0];
      const borrowerId = selectedBorrower
        ? loanSpecificBorrowerId ||
          selectedBorrower.borrowerIds?.[0] ||
          selectedBorrower.borrowerInfoId ||
          dashboardBorrowerId ||
          ''
        : dashboardBorrowerId || loanSpecificBorrowerId || borrower?.borrowerIds?.[0] || borrower?.borrowerInfoId || '';

      if (!borrowerId) {
        this.loggingService.logWarning(
          `[DashboardService] exchangeCpSsoInBackground: no borrowerId resolved for loanId=${loanId}`,
        );
        return;
      }

      const mlaSsoToken = await this.mlaAuthService.getSsoTokenForCp(loanId, borrowerId);

      if (!mlaSsoToken) {
        this.loggingService.logWarning(
          `[DashboardService] exchangeCpSsoInBackground: getSsoTokenForCp returned null for loanId=${loanId}`,
        );
        return;
      }

      const cpResponse = await this.ssoAuthService.login({ ssoToken: mlaSsoToken });

      if (cpResponse?.authToken) {
        this.cpAuthToken = cpResponse.authToken;

        this.authService.setCpContext({
          authToken: cpResponse.authToken,
          loanId: cpResponse.loanId,
          borrowerId: cpResponse.borrowerId,
          userGuid: cpResponse.userGuid,
          expiresInSeconds: cpResponse.expiresInSeconds,
          visitorId: cpResponse.visitorId,
          mlaDomainLink: cpResponse.mlaDomainLink,
          email: cpResponse.email,
        });
      }
    } catch (error) {
      this.loggingService.logException(
        error,
        `[DashboardService] exchangeCpSsoInBackground failed for loanId=${loanId}`,
      );
    }
  }

  async getRecentBankerRefId(): Promise<string | null> {
    try {
      const bankers = await this.mlaDashboardService.getRecentBankers();
      return bankers.length > 0 ? bankers[0] : null;
    } catch (error) {
      this.loggingService.logException(error, '[DashboardService] Error getting recent banker');
      return null;
    }
  }

  getUserAccountsAsync(): Promise<AccountModel[]> {
    return firstValueFrom(this.getUserAccounts());
  }

  loadSingleLoanFirst(loanNumber: string): Promise<void> {
    if (this._loanResponses().length > 0 && this._userProfile()) {
      return Promise.resolve();
    }

    if (this._loadSingleLoanPromise) {
      return this._loadSingleLoanPromise;
    }

    this._loadSingleLoanPromise = this._doLoadSingleLoanFirst(loanNumber).finally(() => {
      this._loadSingleLoanPromise = null;
    });

    return this._loadSingleLoanPromise;
  }

  private async _doLoadSingleLoanFirst(loanNumber: string): Promise<void> {
    let userId = this.authService.getUserId();
    if (!userId && this.env.mockConfig?.enabled) {
      userId = 'mock-user-id-12345';
    }
    if (!userId) {
      return;
    }

    const profile = await this.fetchUserProfile(userId);
    if (!profile) {
      return;
    }

    this._userProfile.set(profile);

    if (profile.firstName || profile.lastName) {
      const selectedBorrowerName = this.mlaAuthService.getSelectedBorrowerName();
      const cachedBorrower = this.mlaBorrowerService.getCachedPrimaryBorrower();
      const resolvedFirstName = selectedBorrowerName?.firstName || cachedBorrower?.firstName || profile.firstName;
      const resolvedLastName = selectedBorrowerName?.lastName || cachedBorrower?.lastName || profile.lastName;
      this.authService.setUserInfo(profile.globalUserID, resolvedFirstName, resolvedLastName, profile.email);
    }

    const allLoanRequests = this.buildLoanRequests(profile);
    if (allLoanRequests.length === 0) {
      return;
    }

    const targetRequest = allLoanRequests.find((r) => r.loanNumber === loanNumber);
    if (!targetRequest) {
      await firstValueFrom(this.getUserAccounts());
      return;
    }

    const loanResponse = await this.loanService.getLoan(targetRequest.loanNumber, targetRequest.encryptionValue);
    const singleLoanResponses = [loanResponse];
    this.setLoanResponses(singleLoanResponses);
    // Partial snapshot: the remaining loans are still in flight, so unlinked-loan
    // reconciliation must wait for `loadRemainingLoansInBackground`.
    this._fullLoanSetLoaded.set(false);
    this.loanSelectionService.setAllLoanResponses(singleLoanResponses, this._userProfile());

    await this.loadPendingPaymentsForLoansAsync(singleLoanResponses);
    this.loadLoanSummaries([targetRequest]).subscribe();
    this.loadLoanMessages([targetRequest]).subscribe();
    this.featureFlagService
      .loadFlags([targetRequest])
      .catch((error) =>
        this.loggingService.logException(error, '[DashboardService] Failed to load feature flags for single loan'),
      );

    this.loadHelocLoanInfo(singleLoanResponses).subscribe();

    this.loadRemainingLoansInBackground(allLoanRequests, loanNumber);
  }

  private loadRemainingLoansInBackground(allLoanRequests: LoanRequestItem[], loadedLoanNumber: string): void {
    this._isLoadingRemainingLoans.set(true);

    from(this.loanService.getLoans(allLoanRequests))
      .pipe(
        map((loanResponses) => this.sortLoanResponsesByType(loanResponses)),
        tap((sortedResponses) => {
          this.setLoanResponses(sortedResponses);
          this._fullLoanSetLoaded.set(true);
          this.loanSelectionService.setAllLoanResponses(sortedResponses, this._userProfile());
        }),
        switchMap((sortedResponses) => {
          const remainingResponses = sortedResponses.filter((lr) => lr.loan.loanNumber !== loadedLoanNumber);
          return from(this.loadPendingPaymentsForLoansAsync(remainingResponses)).pipe(
            switchMap(() => {
              const remainingRequests = allLoanRequests.filter((r) => r.loanNumber !== loadedLoanNumber);
              this.loadLoanSummaries(remainingRequests).subscribe();
              this.loadLoanMessages(allLoanRequests).subscribe();
              this.featureFlagService
                .loadFlags(allLoanRequests)
                .catch((error) =>
                  this.loggingService.logException(error, '[DashboardService] Failed to load feature flags'),
                );
              const remainingHelocResponses = sortedResponses.filter((lr) => lr.loan.loanNumber !== loadedLoanNumber);
              this.loadHelocLoanInfo(remainingHelocResponses).subscribe();
              return of(undefined);
            }),
          );
        }),
        finalize(() => this._isLoadingRemainingLoans.set(false)),
        catchError((error) => {
          this.loggingService.logException(error, '[DashboardService] Failed to load remaining loans in background');
          // The loan set will not get any more complete than the single-loan snapshot,
          // so stop blocking unlinked-loan reconciliation on it.
          this._fullLoanSetLoaded.set(true);
          return of(undefined);
        }),
      )
      .subscribe();
  }

  getOriginationApplicationsAsync(): Promise<OriginationApplicationModel[]> {
    return firstValueFrom(this.getOriginationApplications());
  }

  async exchangeCpSsoForLoan(loanId: string): Promise<void> {
    await this.exchangeCpSsoInBackground(loanId);
  }

  async ensureCpToken(originationAppId?: string): Promise<string | undefined> {
    const existingToken = this.authService.getCpToken();
    const existingLoanId = this.authService.getCpLoanId();
    if (
      existingToken &&
      !this.authService.isCpTokenExpired() &&
      (!originationAppId || existingLoanId === originationAppId)
    ) {
      this.loggingService.logInfo(`[DashboardService] ensureCpToken: reusing existing token, loanId=${existingLoanId}`);
      return existingLoanId ?? undefined;
    }

    if (originationAppId) {
      try {
        await this.exchangeCpSsoInBackground(originationAppId);
        if (this.authService.getCpToken() && !this.authService.isCpTokenExpired()) {
          const loanId = this.authService.getCpLoanId() ?? undefined;
          this.loggingService.logInfo(`[DashboardService] ensureCpToken: MLA exchange succeeded, loanId=${loanId}`);
          return loanId;
        }
        this.loggingService.logWarning(
          '[DashboardService] ensureCpToken: MLA exchange completed but no valid CP token stored',
        );
      } catch (error) {
        this.loggingService.logException(error, '[DashboardService] MLA SSO → CP SSO exchange failed');
      }
    }

    const b2cToken = this.authService.getToken();
    if (b2cToken) {
      try {
        const cpResponse = await this.ssoAuthService.login({ ssoToken: b2cToken });
        if (cpResponse?.authToken) {
          this.cpAuthToken = cpResponse.authToken;
          this.authService.setCpContext({
            authToken: cpResponse.authToken,
            loanId: cpResponse.loanId,
            borrowerId: cpResponse.borrowerId,
            userGuid: cpResponse.userGuid,
            expiresInSeconds: cpResponse.expiresInSeconds,
            visitorId: cpResponse.visitorId,
            mlaDomainLink: cpResponse.mlaDomainLink,
            email: cpResponse.email,
          });
          this.loggingService.logInfo(
            `[DashboardService] ensureCpToken: B2C fallback succeeded, loanId=${cpResponse.loanId}`,
          );
          return cpResponse.loanId;
        }
      } catch (error) {
        this.loggingService.logException(error, '[DashboardService] B2C → CP SSO exchange failed');
      }
    }

    this.loggingService.logWarning('[DashboardService] Could not establish CP token via any path');
    return undefined;
  }

  async getPendingPayments(loanNumber: string): Promise<PendingPaymentApiItem[]> {
    const cached = this._paymentHistoryCache().get(loanNumber)?.pending;
    if (cached) {
      return cached;
    }
    const history = await this.fetchPaymentHistoryForLoan(loanNumber);
    return history?.pending ?? [];
  }

  private getPendingOtpCount(loanNumber: string): number {
    const cached = this._paymentHistoryCache().get(loanNumber)?.pending;
    if (!cached) {
      return 0;
    }
    return cached.filter((p) => p.paymentFrequency === 0).length;
  }

  private computePaymentEligibility(response: LoanResponse, loanNumber: string): boolean {
    const productType = this.mapProductType(loanNumber);
    const isHeloc = productType === 'HELOC';

    const otpCount = this.getPendingOtpCount(loanNumber);
    if (!isHeloc && otpCount >= 3) return false;

    const helocInfo = isHeloc ? this._helocLoanInfoCache().get(loanNumber) : undefined;
    const eligibility = isHeloc ? helocInfo?.oneTimePaymentEligibility : response.oneTimePaymentEligibility;
    if (!eligibility) return isHeloc ? helocInfo !== undefined : true;
    if (eligibility.isEligibile === 'Allowed') return true;

    if (isHeloc) {
      return eligibility.isEligibile !== 'NotAllowed';
    }

    return isOnlyActivePaymentCountError(eligibility) && otpCount < 3;
  }

  async getPaymentHistory(
    loanNumber: string,
  ): Promise<{ posted: PaymentTransaction[]; pending: PendingPaymentApiItem[] } | null> {
    const cached = this._paymentHistoryCache().get(loanNumber);
    if (cached) {
      return cached;
    }
    return this.fetchPaymentHistoryForLoan(loanNumber);
  }

  private async fetchPaymentHistoryForLoan(
    loanNumber: string,
  ): Promise<{ posted: PaymentTransaction[]; pending: PendingPaymentApiItem[] } | null> {
    const profile = this._userProfile();
    if (!profile) {
      return null;
    }

    const encryptionValue = getEncryptionValueForLoan(profile, loanNumber);
    if (!encryptionValue) {
      return null;
    }

    try {
      const history = await this.paymentService.getPaymentHistory(loanNumber, encryptionValue);
      const result = { posted: history.posted || [], pending: history.pending || [] };
      const newCache = new Map(this._paymentHistoryCache());
      newCache.set(loanNumber, result);
      this._paymentHistoryCache.set(newCache);
      return result;
    } catch (error) {
      this.loggingService.logException(error, `[DashboardService] Failed to fetch payment history for ${loanNumber}:`);
      return null;
    }
  }

  invalidatePaymentHistoryCache(loanNumber: string): void {
    const newCache = new Map(this._paymentHistoryCache());
    newCache.delete(loanNumber);
    this._paymentHistoryCache.set(newCache);
  }

  async refreshHelocLoanInfoForLoan(loanNumber: string): Promise<void> {
    const profile = this._userProfile();
    if (!profile) return;

    const encryptionValue = getEncryptionValueForLoan(profile, loanNumber);
    if (!encryptionValue) return;

    this._helocRefreshingLoans.update((current) => {
      const next = new Set(current);
      next.add(loanNumber);
      return next;
    });

    try {
      const info = await this.helocService.getHelocLoanInfo(loanNumber, encryptionValue);
      const newCache = new Map(this._helocLoanInfoCache());
      newCache.set(loanNumber, info);
      this._helocLoanInfoCache.set(newCache);
      this.mergeHelocEligibilityIntoLoanResponses(newCache);
    } catch (error) {
      this.loggingService.logException(error, `[DashboardService] Failed to refresh HELOC loan info for ${loanNumber}`);
    } finally {
      this._helocRefreshingLoans.update((current) => {
        const next = new Set(current);
        next.delete(loanNumber);
        return next;
      });
    }
  }

  updateCachedPaperlessInfo(loanNumber: string, paperlessInfo: Partial<PaperlessInfo>): void {
    const currentResponses = this._loanResponses();
    if (currentResponses.length === 0) return;

    const updatedResponses = currentResponses.map((response) => {
      if (response.loan.loanNumber === loanNumber && response.loan.paperlessInfo) {
        return {
          ...response,
          loan: {
            ...response.loan,
            paperlessInfo: {
              ...response.loan.paperlessInfo,
              ...paperlessInfo,
            },
          },
        };
      }
      return response;
    });

    this.setLoanResponses(updatedResponses);
    this.savePaperlessToStorage(loanNumber, paperlessInfo);
  }

  getSavedPaperlessFromStorage(loanNumber: string): Partial<PaperlessInfo> | null {
    try {
      const raw = sessionStorage.getItem(`paperless_${loanNumber}`);
      if (!raw) return null;
      return JSON.parse(raw) as Partial<PaperlessInfo>;
    } catch {
      return null;
    }
  }

  private savePaperlessToStorage(loanNumber: string, paperlessInfo: Partial<PaperlessInfo>): void {
    try {
      sessionStorage.setItem(`paperless_${loanNumber}`, JSON.stringify(paperlessInfo));
    } catch {
      // sessionStorage may be unavailable in some contexts
    }
  }

  updateUserProfileProperty(property: UserProperty): void {
    this._userProfile.update((profile) => {
      if (!profile) return profile;
      const existingIndex = profile.properties.findIndex((p) => p.key === property.key);
      const updatedProperties =
        existingIndex >= 0
          ? profile.properties.map((p, i) => (i === existingIndex ? { ...p, ...property } : p))
          : [...profile.properties, property];
      return { ...profile, properties: updatedProperties };
    });
  }

  /**
   * Clears the cached account/loan data so the next request refetches from the network.
   *
   * @param options.preserveReadiness When true, the name/welcome readiness flags are left
   * intact so the borrower name keeps its resolved value (rxResource-style) and NO loading
   * placeholder is shown while data reloads. Used for background refreshes (e.g. tab
   * visibility). When omitted/false the readiness is re-armed so the placeholder shows again
   * (initial load, ?reload=1, post-link, logout).
   */
  clearAccountsCache(options?: { preserveReadiness?: boolean }): void {
    this._userProfile.set(null);
    this._loanResponses.set([]);
    // The email lookup results deliberately survive this purge, so the loan set they are
    // reconciled against must be marked stale. Otherwise every funded loan found by email
    // looks unlinked until the refetch lands and the "link my loan" CTA flashes.
    this._fullLoanSetLoaded.set(false);
    this._paymentHistoryCache.set(new Map());
    this._helocLoanInfoCache.set(new Map());
    this._helocLoanInfoLoading.set(false);
    this._helocRefreshingLoans.set(new Set());
    this._helocLoanInfoPendingLoans.set(new Set());
    this.loanSummaryCache.clear();
    if (!options?.preserveReadiness) {
      // Re-arm the name/welcome readiness so the skeleton shows again while the cleared
      // account/origination data reloads, instead of rendering from a stale/auth fallback.
      // The timeout is cleared here and re-armed by the next getUserAccounts/getOriginationApplications.
      this._accountsLoaded.set(false);
      this._originationAppsLoaded.set(false);
      this._readinessTimedOut.set(false);
      if (this.readinessTimeoutHandle) {
        clearTimeout(this.readinessTimeoutHandle);
        this.readinessTimeoutHandle = null;
      }
    }
    this.accountsCache$ = null;
    this.originationCache$ = null;
    this.profileService.clearUserProfileCache();
  }

  clearCache(): void {
    this.clearAccountsCache();

    this._fundedLoanNumbersFoundByEmail.set([]);
    this._originationAppLoanNumbers.set([]);
    this._unlinkedLoansLookupCompleted.set(false);
    this._unlinkedLoansLookupFailed.set(false);
    this._accountsRequested.set(false);

    this.mlaAuthToken = null;
    this.cpAuthToken = null;

    this._loanResponses.set([]);
    // Unlike clearAccountsCache, a full clear changes user (login/logout), so the retained
    // borrower-name source must not survive it.
    this._nameSourceLoanResponses.set([]);
    this._userProfile.set(null);
    this._paymentHistoryCache.set(new Map());
    this._helocLoanInfoCache.set(new Map());
    this._loanMessages.set([]);
    this.loanSummaryCache.clear();

    this.mlaBorrowerService.clearCache();
    this.mlaDashboardService.clearDashboardCache();
    this.loanSelectionService.clearSelection();
    this.featureFlagService.clearFlags();
  }

  private async fetchUserProfile(userId: string): Promise<UserProfile | null> {
    if (this.themeService.isJvThemeActive()) {
      this.loggingService.logInfo('[DashboardService] Skipping LDProfileDomain fetch for JV user');
      return null;
    }

    try {
      return await this.profileService.getUserProfile(userId);
    } catch (error) {
      this.loggingService.logException(error, 'Error fetching user profile:');
      throw error;
    }
  }

  private buildLoanRequests(profile: UserProfile): LoanRequestItem[] {
    const loanNumbers = getLoanNumbersFromProfile(profile);
    const requests: LoanRequestItem[] = [];

    for (const loanNumber of loanNumbers) {
      const encryptionValue = getEncryptionValueForLoan(profile, loanNumber);
      if (encryptionValue) {
        requests.push({ loanNumber, encryptionValue });
      }
    }

    return requests;
  }

  async loadLoansByEmail(): Promise<void> {
    this.startReadinessTimeout();
    // Re-arm the link-a-loan placeholder so it shows the loading state while the lookup
    // re-runs (e.g. on the dashboard visibility refresh), instead of swapping in place.
    this._unlinkedLoansLookupCompleted.set(false);
    this._unlinkedLoansLookupFailed.set(false);

    const email = this.authService.getUserEmail();
    if (!email) {
      this._fundedLoanNumbersFoundByEmail.set([]);
      this._originationAppLoanNumbers.set([]);
      this._unlinkedLoansLookupCompleted.set(true);
      return;
    }

    try {
      // Resolve the email lookup alongside the linked loans (GetLoans) and origination
      // applications, but only decide whether any unlinked loan remains once all three
      // are back. This prevents the "link my loan" prompt from flashing on the dashboard
      // while GetLoans/origination are still loading. Failures in the reconciliation data
      // are non-fatal: they leave that set empty rather than failing the email lookup.
      const [response, , originationApps] = await Promise.all([
        this.loanService.getLoansByEmail(email),
        this.getUserAccountsAsync().catch((error) => {
          this.loggingService.logException(
            error,
            '[DashboardService] Failed to load accounts for unlinked-loan reconciliation',
          );
          return [];
        }),
        this.getOriginationApplicationsAsync().catch((error) => {
          this.loggingService.logException(
            error,
            '[DashboardService] Failed to load origination applications for unlinked-loan reconciliation',
          );
          return [];
        }),
      ]);

      this._originationAppLoanNumbers.set(
        (originationApps ?? [])
          .map((app) => app.loanNumber?.trim())
          .filter((loanNumber): loanNumber is string => !!loanNumber),
      );
      this._fundedLoanNumbersFoundByEmail.set(
        response?.found && response.loanNumbers?.length ? response.loanNumbers : [],
      );
      this._unlinkedLoansLookupFailed.set(false);
      this._unlinkedLoansLookupCompleted.set(true);
    } catch (error) {
      this.loggingService.logException(error, '[DashboardService] Failed to load loans by email');
      this._fundedLoanNumbersFoundByEmail.set([]);
      this._originationAppLoanNumbers.set([]);
      this._unlinkedLoansLookupFailed.set(true);
      this._unlinkedLoansLookupCompleted.set(true);
    }
  }

  private getLastPayment(loanNumber: string): PaymentTransaction | null {
    const history = this._paymentHistoryCache().get(loanNumber);
    if (!history?.posted?.length) return null;
    const sorted = [...history.posted]
      .filter((p) => {
        const status = (p.paymentStatus ?? '').toLowerCase();
        return (
          p.transactionGroup === 'payment_transaction' &&
          p.paymentAmount > 0 &&
          !status.includes('fail') &&
          !status.includes('cancel') &&
          !status.includes('reverse')
        );
      })
      .sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime());
    return sorted[0] ?? null;
  }

  getLastPaymentInfo(loanNumber: string): { lastPaymentAmount?: number; lastPaymentDate?: string } {
    const lastPayment = this.getLastPayment(loanNumber);
    if (!lastPayment) return {};
    return {
      lastPaymentAmount: lastPayment.paymentAmount,
      lastPaymentDate: lastPayment.paymentDate,
    };
  }

  getInterestRateForLoan(loanNumber: string): number | undefined {
    const loanSummary = this.loanSummaryCache.get(loanNumber);
    const loan = this._loanResponses().find((lr) => lr.loan.loanNumber === loanNumber)?.loan;
    return loanSummary?.interestRate ?? loanSummary?.interestRates ?? loan?.annualInterestRate;
  }

  private async loadPendingPaymentsForLoansAsync(loanResponses: LoanResponse[]): Promise<void> {
    return firstValueFrom(this.loadPendingPaymentsForLoans(loanResponses));
  }

  private loadPendingPaymentsForLoans(loanResponses: LoanResponse[]): Observable<void> {
    if (loanResponses.length === 0) {
      return of(undefined);
    }

    const userProfile = this._userProfile();
    const requests = loanResponses.map((response) => {
      const loanNumber = response.loan.loanNumber;
      const encryptionValue = userProfile ? getEncryptionValueForLoan(userProfile, loanNumber) : null;

      if (!encryptionValue) {
        return of({ loanNumber, posted: [] as PaymentTransaction[], pending: [] as PendingPaymentApiItem[] });
      }

      return from(this.paymentService.getPaymentHistory(loanNumber, encryptionValue)).pipe(
        map((history) => ({ loanNumber, posted: history.posted || [], pending: history.pending || [] })),
        catchError((error) => {
          this.loggingService.logException(
            error,
            `[DashboardService] Failed to load payment history for ${loanNumber}`,
          );
          return of({ loanNumber, posted: [] as PaymentTransaction[], pending: [] as PendingPaymentApiItem[] });
        }),
      );
    });

    return forkJoin(requests).pipe(
      tap((results) => {
        const newCache = new Map(this._paymentHistoryCache());
        for (const result of results) {
          newCache.set(result.loanNumber, { posted: result.posted, pending: result.pending });
        }
        this._paymentHistoryCache.set(newCache);
      }),
      map(() => undefined),
    );
  }

  private loadLoanMessages(loanRequests: LoanRequestItem[]): Observable<void> {
    if (loanRequests.length === 0) {
      return of(undefined);
    }

    return from(this.loanService.getLoanMessages(loanRequests)).pipe(
      tap((response) => {
        if (response?.messages) {
          this._loanMessages.set(response.messages);
        }
      }),
      map(() => undefined),
      catchError((error) => {
        this.loggingService.logException(error, '[DashboardService] Failed to load loan messages');
        return of(undefined);
      }),
    );
  }

  private loadLoanSummaries(loanRequests: LoanRequestItem[]): Observable<void> {
    if (loanRequests.length === 0) {
      return of(undefined);
    }

    const requests = loanRequests.map((request) => {
      return from(this.loanService.getLoanSummary(request.loanNumber, request.encryptionValue)).pipe(
        map((summary) => ({ loanNumber: request.loanNumber, summary })),
        catchError((error) => {
          this.loggingService.logException(
            error,
            `[DashboardService] Failed to load loan summary for ${request.loanNumber}`,
          );
          return of(null);
        }),
      );
    });

    return forkJoin(requests).pipe(
      tap((results) => {
        for (const result of results) {
          if (result) {
            this.loanSummaryCache.set(result.loanNumber, result.summary);
          }
        }
      }),
      map(() => undefined),
    );
  }

  private loadHelocLoanInfo(loanResponses: LoanResponse[]): Observable<void> {
    const helocLoans = loanResponses.filter((lr) => this.mapProductType(lr.loan.loanNumber) === 'HELOC');

    if (helocLoans.length === 0) {
      return of(undefined);
    }

    this._helocLoanInfoLoading.set(true);

    const pendingLoanNumbers = new Set(helocLoans.map((lr) => lr.loan.loanNumber));
    this._helocLoanInfoPendingLoans.set(pendingLoanNumbers);

    const userProfile = this._userProfile();
    const requests = helocLoans.map((lr) => {
      const encryptionValue = userProfile ? getEncryptionValueForLoan(userProfile, lr.loan.loanNumber) : null;
      if (!encryptionValue) {
        this.loggingService.logWarning(
          `[DashboardService] No encryption value for HELOC loan ${lr.loan.loanNumber}, skipping`,
        );
        this.removeFromHelocPending(lr.loan.loanNumber);
        return of(null);
      }
      return from(this.helocService.getHelocLoanInfo(lr.loan.loanNumber, encryptionValue)).pipe(
        map((info) => {
          return { loanNumber: lr.loan.loanNumber, info };
        }),
        tap((result) => {
          if (result) {
            const newCache = new Map(this._helocLoanInfoCache());
            newCache.set(result.loanNumber, result.info);
            this._helocLoanInfoCache.set(newCache);
            this.mergeHelocEligibilityIntoLoanResponses(newCache);
          }
          this.removeFromHelocPending(lr.loan.loanNumber);
        }),
        catchError((error) => {
          this.loggingService.logException(
            error,
            `[DashboardService] Failed to load HELOC loan info for ${lr.loan.loanNumber}`,
          );
          this.removeFromHelocPending(lr.loan.loanNumber);
          return of(null);
        }),
      );
    });

    return forkJoin(requests).pipe(
      finalize(() => {
        this._helocLoanInfoLoading.set(false);
        this._helocLoanInfoPendingLoans.set(new Set());
      }),
      map(() => undefined),
    );
  }

  private removeFromHelocPending(loanNumber: string): void {
    this._helocLoanInfoPendingLoans.update((current) => {
      const next = new Set(current);
      next.delete(loanNumber);
      return next;
    });
  }

  private mergeHelocEligibilityIntoLoanResponses(helocCache: Map<string, HelocLoanInfo>): void {
    const currentResponses = this._loanResponses();
    if (currentResponses.length === 0) return;

    let updated = false;
    const updatedResponses = currentResponses.map((lr) => {
      const helocInfo = helocCache.get(lr.loan.loanNumber);
      if (!helocInfo) return lr;

      const hasEligibility =
        helocInfo.recurringPaymentEligibility ||
        helocInfo.biWeeklyRecurringPaymentEligibility ||
        helocInfo.semiMonthlyRecurringPaymentEligibility ||
        helocInfo.oneTimePaymentEligibility;

      if (!hasEligibility) return lr;

      updated = true;

      return {
        ...lr,
        ...(helocInfo.recurringPaymentEligibility && {
          recurringPaymentEligibility: helocInfo.recurringPaymentEligibility,
        }),
        ...(helocInfo.biWeeklyRecurringPaymentEligibility && {
          biweeklyRecurringPaymentEligibility: helocInfo.biWeeklyRecurringPaymentEligibility,
        }),
        ...(helocInfo.semiMonthlyRecurringPaymentEligibility && {
          semiMonthlyRecurringPaymentEligibility: helocInfo.semiMonthlyRecurringPaymentEligibility,
        }),
        ...(helocInfo.oneTimePaymentEligibility && {
          oneTimePaymentEligibility: helocInfo.oneTimePaymentEligibility,
        }),
      };
    });

    if (updated) {
      this.setLoanResponses(updatedResponses);
      this.loanSelectionService.setAllLoanResponses(updatedResponses, this._userProfile());
    }
  }

  private sortLoanResponsesByType(loanResponses: LoanResponse[]): LoanResponse[] {
    const typeOrder: Record<string, number> = { HELOC: 0, HOME_EQUITY: 1, MORTGAGE: 2 };
    return [...loanResponses].sort((a, b) => {
      const typeA = typeOrder[this.mapProductType(a.loan.loanNumber)] ?? 2;
      const typeB = typeOrder[this.mapProductType(b.loan.loanNumber)] ?? 2;
      return typeA - typeB;
    });
  }

  private transformLoansToAccounts(loanResponses: LoanResponse[]): AccountModel[] {
    return loanResponses.map((response) => {
      const loan = response.loan;
      const productType = this.mapProductType(loan.loanNumber);

      const paymentDue = loan.paymentsDue?.[0];
      const principalAndInterest = paymentDue?.principalAndInterest ?? loan.paymentDue * 0.75;
      const taxesAndInsurance = paymentDue?.taxAndInsurance ?? loan.paymentDue * 0.23;

      const lastPayment = this.getLastPayment(loan.loanNumber);

      return {
        id: loan.loanNumber,
        type: productType,
        accountNumber: loan.loanNumber,
        address: getFullAddress(loan.property),
        propertyImage: this.getPropertyImage(),
        originalAmount: loan.loanBalance.beginningPrincipal,
        currentBalance: loan.loanBalance.unpaidPrincipal,
        accountTotal: loan.paymentDue + loan.totalFeeAmount,
        nextPaymentAmount: loan.paymentDue,
        lastPaymentDate: lastPayment ? new Date(lastPayment.paymentDate) : undefined,
        lastPaymentAmount: lastPayment?.paymentAmount,
        nextPaymentDate: loan.paymentDueDate ? new Date(loan.paymentDueDate) : undefined,

        accountStanding: getAccountStanding(loan),
        loanStatus: loan.loanStatus,
        daysPastDueStatus: loan.daysPastDueStatus,
        isPaidInFull: loan.isPaidInFull,
        isTransferredToCenlar: loan.isTransferredToCenlar,
        isTransferredToThirdParty: loan.isTransferredToThirdParty,
        isEarlyTransferToCenlar: loan.isEarlyTransferToCenlar,
        transferDate: loan.transferDate,
        newServicer: loan.newServicer,
        newServicerPhone: loan.newServicerPhone,
        loanTransferredDays: loan.loanTransferredDays,
        isPIITServiceTransfer: loan.isPIITServiceTransfer,
        isLoanStatusHidden: loan.isLoanStatusHidden ?? false,

        isPaymentEligible: this.computePaymentEligibility(response, loan.loanNumber),

        planDescription: this.computePlanDescription(loan),

        isActive: loan.isActive,
        processStopCode: loan.processStopCode,
        manCode: loan.mitigationInfo?.manCode ?? loan.manCode ?? null,
        isCh7Discharge: loan.isCh7Discharge ?? false,
        isCh11_12_13Discharge: loan.isCh11_12_13Discharge ?? false,
        hasActiveMitigationPlan: !!(
          loan.mitigationInfo?.isActiveForbearancePlan ||
          loan.mitigationInfo?.isActiveRepaymentPlan ||
          loan.mitigationInfo?.isActiveTrialPlan
        ),

        principalAndInterest,
        taxesAndInsurance,
        fees: loan.totalFeeAmount,
        escrowBalance: loan.loanBalance.escrowBalance,
        fhaPaymentSupplement: loan.fhaPaymentSupplement ?? undefined,
        buydownOrSubsidy: loan.buydownOrSubsidy ?? undefined,
        njFireFeeAmount: loan.njFireFeeAmount ?? undefined,
        replacementReserveBalance: loan.replacementReserveBalance ?? undefined,
        miscellaneousAmount: loan.miscellaneousAmount ?? undefined,

        paymentPrincipal: loan.paymentPrincipal ?? principalAndInterest * 0.3,
        paymentInterest: loan.paymentInterest ?? principalAndInterest * 0.7,

        beginningPrincipal: loan.loanBalance.beginningPrincipal,
        principalPaid: loan.loanBalance.principalPaid,
        unpaidPrincipal: loan.loanBalance.unpaidPrincipal,

        autopayStatus: this.hasAutopayPayments(loan.loanNumber) ? 'Active' : 'Off',

        canRequestPayoffQuote: loan.canRequestPayoffQuote,

        ...this.getHelocFieldsForLoan(loan),
      };
    });
  }

  private getHelocFieldsForLoan(loan: LoanResponse['loan']): Partial<AccountModel> {
    const loanSummary = this.loanSummaryCache.get(loan.loanNumber);
    const helocInfo = this._helocLoanInfoCache().get(loan.loanNumber);
    const interestRate =
      loanSummary?.interestRate ??
      loanSummary?.interestRates ??
      helocInfo?.annualInterestRate ??
      loan.annualInterestRate;

    const accountStanding = getAccountStanding(loan);
    const accountTotal = loan.paymentDue + loan.totalFeeAmount;

    return {
      availableCredit: helocInfo?.availableHELOCAmount ?? loan.availableHELOCAmount,
      creditLimit: helocInfo?.helocLimitAmount ?? loan.helocLimitAmount,
      interestRate,
      ytdInterestPaid: helocInfo?.ytdInterestPaid ?? loan.ytdInterestPaid,
      priorYearInterestPaid: helocInfo?.priorYearInterestPaid ?? loan.priorYearInterestPaid,
      openDate: helocInfo?.noteDate
        ? new Date(helocInfo.noteDate)
        : loan.openDate
          ? new Date(loan.openDate)
          : loan.fundingDate
            ? new Date(loan.fundingDate)
            : undefined,
      asOfDate: loan.asOfDate ? new Date(loan.asOfDate) : undefined,
      showReturnAfter16thMessage: helocInfo?.showReturnAfter16thMessage ?? loan.showReturnAfter16thMessage,
      minDrawAmount: helocInfo?.minDrawAmount ?? loan.minDrawAmount,
      drawEligibilityStatus: this.deriveDrawEligibility(helocInfo, loan),
      ...(helocInfo
        ? {
            nextPaymentAmount: this.computeHelocNextPaymentAmount(helocInfo, loan, accountStanding, accountTotal),
            ...(helocInfo.nextPaymentDueDate ? { nextPaymentDate: new Date(helocInfo.nextPaymentDueDate) } : {}),
          }
        : {}),
      remainingTerm: helocInfo?.remainingTerm ?? loan.remainingTerm,
      originalTerm: helocInfo?.originalTerm ?? loan.originalTerm,
      maturityDate: helocInfo?.loanMaturityDate
        ? new Date(helocInfo.loanMaturityDate)
        : loan.loanMaturityDate
          ? new Date(loan.loanMaturityDate)
          : undefined,
      currentInterestAmount: helocInfo?.currentInterestAmount ?? loan.currentInterestAmount,
      totalPrincipalInterestAmount: helocInfo?.totalPrincipalInterestAmount ?? loan.totalPrincipalInterestAmount,
      isBillGenerated: helocInfo?.isBillGenerated ?? loan.isBillGenerated,
      helocTotalFeeAmount: helocInfo?.totalFeeAmount ?? loan.totalFeeAmount,
      isHelocEligible: loan.isHelocEligible,
    };
  }

  private computeHelocNextPaymentAmount(
    helocInfo: HelocLoanInfo | undefined,
    loan: LoanResponse['loan'],
    accountStanding: string,
    accountTotal: number,
  ): number {
    if (this.isHelocInHouse(helocInfo)) {
      return helocInfo?.paymentDue ?? loan.paymentDue;
    }

    if (!this.shouldShowHelocAmountDue(helocInfo, accountStanding)) {
      return 0;
    }

    if (accountStanding === 'On Time') {
      return helocInfo?.amountDueOnTime ?? loan.paymentDue;
    }
    if (accountStanding === 'Delinquent') {
      return helocInfo?.paymentDue ?? helocInfo?.totalPaymentDue ?? accountTotal;
    }
    return helocInfo?.totalPaymentDue ?? accountTotal;
  }

  private isHelocInHouse(helocInfo: HelocLoanInfo | undefined): boolean {
    return isHelocInHouseUtil(helocInfo, this.envFeatureService.isEnabled('HelocInHousePayment'));
  }

  private shouldShowHelocAmountDue(helocInfo: HelocLoanInfo | undefined, accountStanding: string): boolean {
    return shouldShowHelocAmountDueUtil(helocInfo, accountStanding);
  }

  private deriveDrawEligibility(
    helocInfo: HelocLoanInfo | undefined,
    loan: LoanResponse['loan'],
  ): HelocDrawEligibilityEnum {
    return deriveDrawEligibilityUtil(helocInfo, loan.enableDraws !== false);
  }

  computeHelocPaymentEligibility(loanNumber: string): boolean {
    const helocInfo = this._helocLoanInfoCache().get(loanNumber);
    if (!helocInfo) return false;
    const eligibility = helocInfo.oneTimePaymentEligibility;
    if (!eligibility) return true;
    if (eligibility.isEligibile === 'Allowed') return true;
    return eligibility.isEligibile !== 'NotAllowed';
  }

  getHelocPaymentFields(loanNumber: string): { nextPaymentAmount: number; nextPaymentDate?: Date } | undefined {
    const helocInfo = this._helocLoanInfoCache().get(loanNumber);
    if (!helocInfo) return undefined;

    const loanResponse = this._loanResponses().find((lr) => lr.loan.loanNumber === loanNumber);
    if (!loanResponse) return undefined;

    const loan = loanResponse.loan;
    const accountStanding = getAccountStanding(loan);
    const accountTotal = loan.paymentDue + loan.totalFeeAmount;
    const nextPaymentAmount = this.computeHelocNextPaymentAmount(helocInfo, loan, accountStanding, accountTotal);
    const nextPaymentDate = helocInfo.nextPaymentDueDate ? new Date(helocInfo.nextPaymentDueDate) : undefined;

    return { nextPaymentAmount, nextPaymentDate };
  }

  getDrawEligibilityErrorMessage(loanNumber: string): string | undefined {
    const helocInfo = this._helocLoanInfoCache().get(loanNumber);
    const drawCheck = helocInfo?.eligibilityCheck?.find((ec) => ec.type === 'Draw');
    return drawCheck?.eligibility?.errors?.[0]?.errorMessage ?? undefined;
  }

  isDrawDisasterError(loanNumber: string): boolean {
    const helocInfo = this._helocLoanInfoCache().get(loanNumber);
    const drawCheck = helocInfo?.eligibilityCheck?.find((ec) => ec.type === 'Draw');

    // When a draw eligibility check exists, rely on the API error to determine
    // if the ineligibility is specifically due to a disaster. This avoids
    // showing the disaster message when the loan is ineligible for other
    // reasons (e.g. delinquency) even if the advance stop code is 8 or 9.
    if (drawCheck) {
      const errorMessage = drawCheck.eligibility.errors?.[0]?.errorMessage;
      return errorMessage?.includes('SEND_SECURE_MESSAGE_DISASTER') ?? false;
    }

    // Fallback when no draw eligibility check is available: use advance stop code
    const advanceStop = helocInfo?.helocAdvanceStop;
    return advanceStop === '8' || advanceStop === '9';
  }

  computePlanDescription(loan: LoanResponse['loan']): string | undefined {
    const mi = loan.mitigationInfo;
    if (!mi) return undefined;

    const installments = mi.planInstallmentInfo;
    const totalInstallments = installments?.length ?? 0;

    if (mi.isActiveRepaymentPlan) {
      const currentInstallment =
        totalInstallments > 0 ? (installments?.filter((inst) => !!inst.statusCode).length ?? 0) + 1 : 0;
      return totalInstallments > 0
        ? `Your loan is currently on an Active Repayment Plan - Installment ${currentInstallment} of ${totalInstallments} is due`
        : 'Your loan is currently on an Active Repayment Plan';
    }

    if (mi.isActiveTrialPlan) {
      const currentInstallment =
        totalInstallments > 0 ? (installments?.filter((inst) => !!inst.statusCode).length ?? 0) + 1 : 0;
      return totalInstallments > 0
        ? `Your loan is currently approved for a Modification Trial Plan - Installment ${currentInstallment} of ${totalInstallments} is due`
        : 'Your loan is currently approved for a Modification Trial Plan';
    }

    if (mi.isActiveForbearancePlan) {
      const isPlanBrokenAndDueFuture =
        mi.isPlanBroken && loan.paymentDueDate && new Date(loan.paymentDueDate) >= new Date();
      if (isPlanBrokenAndDueFuture) return undefined;

      const endDate = mi.lastBusinessDayOfTheDefaultPlan;
      return endDate
        ? `Your loan is currently on an Active Forbearance Plan (Ends on ${new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`
        : 'Your loan is currently on an Active Forbearance Plan';
    }

    return undefined;
  }

  mapProductType(loanNumber: string): 'MORTGAGE' | 'HELOC' | 'HOME_EQUITY' {
    const profileLoanType = getLoanTypeFromProfile(loanNumber, this._userProfile());
    const type = profileLoanType.toUpperCase();

    if (type === 'HELOC') {
      return 'HELOC';
    }
    if (type === 'HOME_EQUITY' || type === 'HOMEEQUITY') {
      return 'HOME_EQUITY';
    }
    return 'MORTGAGE';
  }

  private getPropertyImage(): string {
    return 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400';
  }

  hasAutopayPayments(loanNumber: string): boolean {
    const pending = this._paymentHistoryCache().get(loanNumber)?.pending;
    if (!pending) return false;
    return pending.some(
      (p: PendingPaymentApiItem) =>
        p.paymentFrequency !== null && p.paymentFrequency > 0 && p.paymentStatus !== PaymentProcessingStatus.Delete,
    );
  }

  getLastDeletedAutopayDate(loanNumber: string): string | null {
    const pending = this._paymentHistoryCache().get(loanNumber)?.pending;
    if (!pending) return null;

    const deletedAutopays = pending
      .filter(
        (p: PendingPaymentApiItem) =>
          p.paymentFrequency !== null && p.paymentFrequency > 0 && p.paymentStatus === PaymentProcessingStatus.Delete,
      )
      .sort((a, b) => new Date(b.updatedAt ?? b.createdAt).getTime() - new Date(a.updatedAt ?? a.createdAt).getTime());

    return deletedAutopays.length > 0 ? (deletedAutopays[0].updatedAt ?? deletedAutopays[0].createdAt) : null;
  }
}
